import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

const buttonVariants = cva(
    "bb:inline-flex bb:items-center bb:justify-center bb:gap-2 bb:whitespace-nowrap bb:rounded-md bb:text-sm bb:font-medium bb:ring-offset-background bb:transition-colors focus-visible:bb:outline-none focus-visible:bb:ring-2 focus-visible:bb:ring-ring focus-visible:bb:ring-offset-2 disabled:bb:pointer-events-none disabled:bb:opacity-50 [&_svg]:bb:pointer-events-none [&_svg]:bb:size-4 [&_svg]:bb:shrink-0",
    {
        variants: {
            variant: {
                default: "bb:bg-primary bb:text-primary-foreground hover:bb:bg-primary/90",
                destructive: "bb:bg-destructive bb:text-destructive-foreground hover:bb:bg-destructive/90",
                outline:
                    "bb:border bb:border-input bb:bg-background hover:bb:bg-accent hover:bb:text-accent-foreground",
                secondary: "bb:bg-secondary bb:text-secondary-foreground hover:bb:bg-secondary/80",
                ghost: "hover:bb:bg-accent hover:bb:text-accent-foreground",
                link: "bb:text-primary bb:underline-offset-4 hover:bb:underline",
            },
            size: {
                default: "bb:h-10 bb:px-4 bb:py-2",
                sm: "bb:h-9 bb:rounded-md bb:px-3",
                lg: "bb:h-11 bb:rounded-md bb:px-8",
                icon: "bb:h-10 bb:w-10",
            },
        },
        defaultVariants: {
            variant: "default",
            size: "default",
        },
    },
);

export type ButtonProps = {
  asChild?: boolean;
} & React.ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants>;

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
